import React, { useState, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const ContactSchema = Yup.object().shape({
  name: Yup.string().max(255, "Muito longo"),
  number: Yup.string()
    .required("Número de telefone é obrigatório")
    .min(10, "Número muito curto")
    .max(20, "Número muito longo"),
  var1: Yup.string().max(255, "Muito longo"),
  var2: Yup.string().max(255, "Muito longo"),
  var3: Yup.string().max(255, "Muito longo"),
  var4: Yup.string().max(255, "Muito longo"),
  var5: Yup.string().max(255, "Muito longo"),
  tags: Yup.string().max(255, "Muito longo"),
});

const ContactListItemProModal = ({
  open,
  onClose,
  contactListProId,
  onSuccess,
  contact: initialContact,
}) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isEdit = Boolean(initialContact?.id);

  const initialState = isEdit
    ? {
        name: initialContact.name || "",
        number: initialContact.number || "",
        var1: initialContact.var1 || "",
        var2: initialContact.var2 || "",
        var3: initialContact.var3 || "",
        var4: initialContact.var4 || "",
        var5: initialContact.var5 || "",
        tags: initialContact.tags || "",
        optedIn: initialContact.optedIn !== undefined ? initialContact.optedIn : true,
      }
    : {
        name: "",
        number: "",
        var1: "",
        var2: "",
        var3: "",
        var4: "",
        var5: "",
        tags: "",
        optedIn: true,
      };

  const handleSaveContact = async (values, { setSubmitting }) => {
    try {
      const payload = {
        ...values,
        name: values.name || null,
        var1: values.var1 || null,
        var2: values.var2 || null,
        var3: values.var3 || null,
        var4: values.var4 || null,
        var5: values.var5 || null,
        tags: values.tags || null,
      };

      if (isEdit) {
        await api.put(`/contact-lists-pro/${contactListProId}/items/${initialContact.id}`, payload);
        toast.success("Contato atualizado com sucesso!");
      } else {
        await api.post(`/contact-lists-pro/${contactListProId}/items`, payload);
        toast.success("Contato adicionado com sucesso!");
      }
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? "Editar Contato" : "Adicionar Contato"}</DialogTitle>
      <Formik
        key={isEdit ? initialContact.id : "new"}
        initialValues={initialState}
        validationSchema={ContactSchema}
        onSubmit={handleSaveContact}
        enableReinitialize
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="name"
                    label="Nome"
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="number"
                    label="Número de Telefone *"
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    required
                    error={touched.number && Boolean(errors.number)}
                    helperText={touched.number && errors.number || "Ex: 5511999999999"}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="var1"
                    label="Variável 1"
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    error={touched.var1 && Boolean(errors.var1)}
                    helperText={touched.var1 && errors.var1}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="var2"
                    label="Variável 2"
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    error={touched.var2 && Boolean(errors.var2)}
                    helperText={touched.var2 && errors.var2}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="var3"
                    label="Variável 3"
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    error={touched.var3 && Boolean(errors.var3)}
                    helperText={touched.var3 && errors.var3}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="var4"
                    label="Variável 4"
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    error={touched.var4 && Boolean(errors.var4)}
                    helperText={touched.var4 && errors.var4}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="var5"
                    label="Variável 5"
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    error={touched.var5 && Boolean(errors.var5)}
                    helperText={touched.var5 && errors.var5}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="tags"
                    label="Tags"
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    error={touched.tags && Boolean(errors.tags)}
                    helperText={touched.tags && errors.tags}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.optedIn}
                        onChange={(e) => setFieldValue("optedIn", e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Autorizado para receber campanhas (Opt-in)"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting && <CircularProgress size={20} className={classes.buttonProgress} />}
                {isEdit ? "Salvar" : "Adicionar"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ContactListItemProModal;

